import {
  PROD_HOSTNAME_DEV,
  PROD_HOSTNAME_DEVIP,
  PROD_HOSTNAME_GLOBAL,
  PROD_HOSTNAME_INTLGAME,
  PROD_HOSTNAME_LOCAL,
  PROD_HOSTNAME_PRE,
  PROD_HOSTNAME_PRE_WOA,
  PROD_HOSTNAME_TEST,
  PROD_HOSTNAME_WOA,
} from '@home/components/VersionPopup/_config';
import {
  FilterPageNameContext,
  FilterPageSourceContext,
} from '@op/components/ContentBody/context';
import { NO_DATE } from '@op/constants';
import {
  ExcludeFilterSourceMap,
  ExcludeGuideSourceMap,
  IncludeFilterSourceMap,
  PAGE_NAME,
} from '@op/constants/page';
import {
  getGlobalExclusiveGroup,
  getGlobalPageSourceRule,
  getOpinionFilterCond,
  listMenu,
} from '@op/services';
import { gameInfoType } from '@op/types/common';
import { useContext, useMemo } from 'react';
import { useSelector } from 'umi';
import DBO_FWC from './useFrontWebConfig';
import { DBO_GAME } from '@op/utils/format';
import { subUnRequestType } from './useFrontWebConfig/defaultConfig';
import { updateFilterCondStorage } from '@op/components/FilterCommon/utils/storage';
import {
  setRegionCountryDict,
  transformRegionCountryToDict,
} from '@/pages/opinion/utils/regionCountryDict';

export const DEFAULT_PAGE_URL = '/opinion/Overview/KeyMetrics';

type SourceKeyListType =
  | 'excludeFilter'
  | 'includeFilter'
  | 'excludePageSource'
  | 'excludeGuideSource'
  | 'sentiment'
  | 'sentiment_clause'
  | 'GPT_Btn'
  | 'Sentiment_Rating'
  | 'Keyword_Color'
  | 'Sentiment_Update'
  | 'Sentiment_Tab'
  | 'Go_State_Show'
  | 'Guide_Overview'
  | 'Guide_Competitor'
  | 'Generate_Repeat_Report'
  | 'Update_Repeat_Report'
  | 'Competitor_Rank_Hide';

export const defaultPageList = [
  {
    pageName: PAGE_NAME.REALTIME_OPINIONS,
    url: '/opinion/Overview/RealtimeOpinions',
  },
  { pageName: PAGE_NAME.KEY_METRICS, url: '/opinion/Overview/KeyMetrics' },
  { pageName: PAGE_NAME.KEY_OPINIONS, url: '/opinion/Overview/KeyOpinions' },
  { pageName: PAGE_NAME.FEEDS, url: '/opinion/Feeds/Feeds' },
];

export const PROD_HOSTNAME_TEST2 = 'databrain-test2.intlgame.com';

const pathnameMapEnv: any = {
  [PROD_HOSTNAME_GLOBAL]: 'global',
  [PROD_HOSTNAME_INTLGAME]: 'pro',
  [PROD_HOSTNAME_WOA]: 'woa',
  [PROD_HOSTNAME_PRE_WOA]: 'pre',
  [PROD_HOSTNAME_PRE]: 'pre',
  [PROD_HOSTNAME_TEST]: 'test',
  [PROD_HOSTNAME_TEST2]: 'test2',
  [PROD_HOSTNAME_DEVIP]: 'dev',
  [PROD_HOSTNAME_DEV]: 'dev',
  [PROD_HOSTNAME_LOCAL]: 'test',
};

export function getWebEnv() {
  const pathname = location.hostname;
  return pathnameMapEnv[pathname] || 'pro';
}

type EmptyType = 'permissions' | 'page' | 'lock' | 'noGame';
const emptyTipsMap: {
  [key in EmptyType]: {
    emptyTitleText: string;
    emptyDescText: string;
  };
} = {
  noGame: {
    emptyTitleText: NO_DATE.EMPTY,
    emptyDescText: `This game is not yet configured in the Opinions system. You can contact {email} to add this game.`,
  },
  permissions: {
    emptyTitleText: NO_DATE.EMPTY,
    emptyDescText: `You are unable to view the game data currently, please feel free to contact {contact} for permissions.`,
  },
  page: {
    emptyTitleText: NO_DATE.EMPTY,
    emptyDescText:
      'The page you are trying to access cannot be found. Please check the page link.',
  },
  lock: {
    emptyTitleText:
      'The Game Group has configured permission control for this page. ',
    emptyDescText: `If necessary, please contact {contact}.`,
  },
};
export function useGetPageRule({
  menuName,
  parentPageName,
}: {
  menuName?: string;
  parentPageName?: string;
}) {
  const pageConfigInfo: {
    showTips: boolean;
    emptyTitleText?: string;
    emptyDescText?: string;
    wholeGameInfo: any;
    menuId?: string;
    loading: boolean;
    noPage?: boolean;
  } = useSelector(({ opinionCommon }: any) => {
    const { listMenu, wholeGameInfo, listMenuLoading } = opinionCommon;
    const { opinion_permissions, edition_unified_id, opinion_game } =
      wholeGameInfo;
    if (!opinion_permissions || !opinion_game || !edition_unified_id) {
      const key: EmptyType =
        !edition_unified_id || !opinion_game ? 'noGame' : 'permissions';

      return {
        showTips: true,
        wholeGameInfo,
        loading: listMenuLoading,
        ...emptyTipsMap[key],
      };
    }

    if (!menuName || listMenu.length === 0) {
      return {
        showTips: false,
        wholeGameInfo,
        loading: listMenuLoading,
      };
    }

    const menuItem = DBO_GAME.findMenu(listMenu, menuName, {
      parentMenuName: parentPageName,
      key: 'name_en',
    });
    const noPage = menuItem === null;
    const hasLock = menuItem?.has_permissions === '-1';

    return {
      wholeGameInfo,
      menuId: menuItem?.id,
      showTips: hasLock || noPage,
      loading: listMenuLoading,
      noPage,
      ...(noPage ? emptyTipsMap.page : emptyTipsMap.lock),
    };
  });

  return pageConfigInfo;
}

/**
 * TODO: 其他系统使用
 * 获取单页权限规则
 * @param keys SourceKeyListType 或者 SourceKeyListType数组
 * @returns
 */
export function useGetSingePageSourceRule({
  keys,
}: {
  keys?: SourceKeyListType | Array<SourceKeyListType>;
}): { [key in SourceKeyListType]?: any } {
  const { excludeSource } = useContext(FilterPageSourceContext);
  const { menuId } = useContext(FilterPageNameContext);

  const {
    excludeFilter,
    excludePageSource,
    excludeGuideSource,
    includeFilter,
  } = useMemo(() => {
    const currentPageSource = excludeSource.filter(
      item => item.menu_id === menuId || item.menu_id === '',
    );

    const excludeFilter: Array<string> = [];
    const includeFilter: Array<string> = [];
    const excludePageSource: Array<string> = [];
    const excludeGuideSource: Array<string> = [];

    currentPageSource.map(item => {
      const key = item.source;
      const value = ExcludeFilterSourceMap[key];
      const value2 = ExcludeGuideSourceMap[key];
      const value3 = IncludeFilterSourceMap[key];
      if (value) excludeFilter.push(value);
      else if (value2) excludeGuideSource.push(value2);
      else if (value3) includeFilter.push(value3);
      else excludePageSource.push(key);
    });

    return {
      excludeFilter,
      excludePageSource,
      excludeGuideSource,
      includeFilter,
    };
  }, []);

  const excludeSourceInfo = useMemo(() => {
    if (!keys) return {};
    const keyList = Array.isArray(keys) ? keys : [keys];
    const sourceMap: { [key in SourceKeyListType]?: any } = keyList.reduce(
      (pre, cur) => ({ ...pre, [cur]: excludePageSource.includes(cur) }),
      {},
    );
    return sourceMap;
  }, [excludePageSource]);

  return {
    excludeFilter,
    includeFilter,
    excludePageSource,
    excludeGuideSource,
    ...excludeSourceInfo,
  };
}

const defaultResult = () => ({
  url: DEFAULT_PAGE_URL,
  listMenu: [],
  excludeSource: [],
  hidePageIds: [],
  hideGameListMenu: false
});

export function getListMenuAndIndexPage(gameInfo: gameInfoType) {
  const { edition_unified_id, id_type, entity_type, release_time } =
    gameInfo || {};
  if (edition_unified_id) {
    const params = {
      web_env: getWebEnv(),
      edition_unified_id,
      id_type,
      entity_type,
    };
    return Promise.all([
      listMenu({ ...params, release_time }),
      getGlobalPageSourceRule(params), 
      getOpinionFilterCond({
        ...params,
        un_request_type: ['topic_trans'],
        request_type: ['front_web_config', 'region_country'],
        sub_un_request_type: subUnRequestType,
      }),
      getGlobalExclusiveGroup(),
    ])
      .then(([listMenu, pageSource, fontWebConfig, exclusiveGroup]: Array<any>) => {
        DBO_FWC.init(fontWebConfig?.front_web_config);

        // 预取 region_country：切游戏主路径下，页面首屏 render 前即就绪，
        // 根治「翻译函数首次调用时 store 为空 → 兜底」的时序问题（借鉴 DBO_FWC 先例）。
        setRegionCountryDict(
          transformRegionCountryToDict(fontWebConfig?.region_country),
        );
        // 写 sessionStorage，保证后续过滤器组件挂载时直接命中缓存、不再重复请求
        updateFilterCondStorage(gameInfo, {
          region_country: fontWebConfig?.region_country,
        });

        const { not_permissions_menu_ids, not_permissions_page_source } =
          pageSource || {};

        let url = '/opinion';
        defaultPageList.every(item => {
          const menuItem = DBO_GAME.findMenu(listMenu, item.pageName);
          if (menuItem) {
            url = item.url;
          }
          return !menuItem;
        });
        return {
          url,
          listMenu,
          excludeSource: not_permissions_page_source || [],
          hidePageIds: not_permissions_menu_ids || [],
          hideGameListMenu: typeof exclusiveGroup?.game_list_menu === "boolean" ? !exclusiveGroup?.game_list_menu : false
        };
      })
      .catch(() => defaultResult());
  }
  return defaultResult();
}
